/**
 * /erp/raw-materials/{id} — view + edit + delete (Manager+).
 *
 * Mirrored raw materials (those linked from a product) are read-only
 * here; the manager edits the product instead. The detail page
 * surfaces a banner explaining that and links back to the product.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  ExternalLink,
  Loader2,
  Pencil,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";

import {
  ApiError,
  type ErpRawMaterial,
  type ErpRawMaterialUpdate,
  erpApi,
  isErpCatalogConflict,
} from "../lib/api";
import {
  ERP_QUERY_KEYS,
  groupUomsByCategory,
  useProducts,
  useRawMaterial,
  useUoms,
} from "../lib/erp";
import { useAuthStore } from "../lib/auth";
import { toast } from "../lib/toast";
import { CustomFieldsPanel } from "../components/custom-fields/CustomFieldsPanel";

export const Route = createFileRoute("/_app/erp/raw-materials/$rawMaterialId")({
  component: RawMaterialDetailPage,
});

function RawMaterialDetailPage() {
  const { rawMaterialId } = useParams({
    from: "/_app/erp/raw-materials/$rawMaterialId",
  });
  const rawQ = useRawMaterial(rawMaterialId);
  const productsQ = useProducts();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isProduction = useAuthStore((state) => state.user?.role === "production");

  const mirroringProduct = useMemo(() => {
    if (!rawMaterialId) return undefined;
    return (productsQ.data?.items ?? []).find((p) => p.linked_raw_material_id === rawMaterialId);
  }, [productsQ.data, rawMaterialId]);

  const removeMutation = useMutation({
    mutationFn: () => erpApi.rawMaterials.remove(rawMaterialId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ERP_QUERY_KEYS.rawMaterials });
      toast.success("Raw material deleted.");
      navigate({ to: "/erp/raw-materials" });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409 && isErpCatalogConflict(err.detail)) {
        toast.error(err.detail.message);
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not delete.";
      toast.error(msg);
    },
  });

  if (rawQ.isLoading) {
    return <div className="mx-auto max-w-3xl p-6 text-sm opacity-60">Loading…</div>;
  }
  if (rawQ.error) {
    return (
      <div className="mx-auto max-w-3xl p-6 text-sm text-red-700 dark:text-red-300">
        Could not load raw material: {(rawQ.error as Error).message}
      </div>
    );
  }
  const raw = rawQ.data;
  if (!raw) return null;

  const handleDelete = () => {
    const ok = window.confirm(`Delete raw material ${raw.code}? This cannot be undone.`);
    if (ok) removeMutation.mutate();
  };

  const isMirrored = !!mirroringProduct;

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6">
      <header className="space-y-2">
        <Link
          to="/erp/raw-materials"
          className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100"
        >
          <ArrowLeft className="size-3" aria-hidden />
          Back to raw materials
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <h1 className="truncate text-2xl font-semibold tracking-tight">{raw.name}</h1>
            <p className="font-mono text-xs opacity-70">{raw.code}</p>
          </div>
          {!isProduction && <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDelete}
              disabled={removeMutation.isPending || isMirrored}
              title={isMirrored ? "Detach from product first" : undefined}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-950/70"
            >
              {removeMutation.isPending ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
              ) : (
                <Trash2 className="size-3.5" aria-hidden />
              )}
              Delete
            </button>
          </div>}
        </div>
      </header>

      {isMirrored && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <div className="flex-1">
            This raw material is mirrored from product{" "}
            <Link
              to="/erp/products/$productId"
              params={{ productId: mirroringProduct.id }}
              className="inline-flex items-center gap-0.5 font-medium underline-offset-2 hover:underline"
            >
              {mirroringProduct.code}
              <ExternalLink className="size-3" aria-hidden />
            </Link>
            . Direct edits to name, UoM, or status are disabled — edit the product to change them.
          </div>
        </div>
      )}

      <RawEditor raw={raw} disabled={isMirrored} isProduction={isProduction} />
      <CustomFieldsPanel entity="raw_materials" recordKey={raw.id} />
    </div>
  );
}

function RawEditor({
  raw,
  disabled,
  isProduction,
}: {
  raw: ErpRawMaterial;
  disabled: boolean;
  isProduction: boolean;
}) {
  const queryClient = useQueryClient();
  const uomsQ = useUoms();

  const updateMutation = useMutation({
    mutationFn: (body: ErpRawMaterialUpdate) => erpApi.rawMaterials.update(raw.id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ERP_QUERY_KEYS.rawMaterials });
      queryClient.invalidateQueries({ queryKey: ERP_QUERY_KEYS.rawMaterial(raw.id) });
      toast.success("Saved.");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409 && isErpCatalogConflict(err.detail)) {
        toast.error(err.detail.message);
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not save.";
      toast.error(msg);
    },
  });

  const groupedUoms = groupUomsByCategory(uomsQ.data?.items ?? []);

  return (
    <div className="space-y-3 rounded-xl border border-[var(--border)] p-5">
      {!isProduction && <Row label="Status">
        <button
          type="button"
          onClick={() => updateMutation.mutate({ is_active: !raw.is_active })}
          disabled={updateMutation.isPending || disabled}
          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors ${
            raw.is_active
              ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-900/60"
              : "bg-black/10 opacity-80 hover:bg-black/15 dark:bg-white/10 dark:hover:bg-white/15"
          } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
        >
          {raw.is_active ? "Active — click to deactivate" : "Inactive — click to activate"}
        </button>
      </Row>}

      {!isProduction && <InlineTextField
        label="Name"
        value={raw.name}
        onSave={(v) => updateMutation.mutate({ name: v })}
        pending={updateMutation.isPending}
        disabled={disabled}
      />}

      <InlineSelectField
        label="Unit of measure"
        value={raw.uom}
        groups={groupedUoms}
        onSave={(v) => updateMutation.mutate({ uom: v })}
        pending={updateMutation.isPending}
        disabled={disabled}
      />

      <InlineTextField
        label="Notes"
        value={raw.notes ?? ""}
        placeholder="—"
        multiline
        onSave={(v) => updateMutation.mutate({ notes: v.trim() ? v.trim() : null })}
        pending={updateMutation.isPending}
        disabled={false /* notes always editable, even on mirrored rows */}
      />

      <Row label="Created">
        <span className="text-xs opacity-60">{new Date(raw.created_at).toLocaleString()}</span>
      </Row>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-[10rem_1fr]">
      <div className="pt-1 text-xs font-medium opacity-70">{label}</div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function InlineTextField({
  label,
  value,
  onSave,
  pending,
  placeholder,
  multiline,
  disabled,
}: {
  label: string;
  value: string;
  onSave: (v: string) => void;
  pending: boolean;
  placeholder?: string;
  multiline?: boolean;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (editing && !disabled) {
    return (
      <Row label={label}>
        <div className="flex items-start gap-2">
          {multiline ? (
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)]"
            />
          ) : (
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)]"
            />
          )}
          <button
            type="button"
            onClick={() => {
              if (draft !== value) onSave(draft);
              setEditing(false);
            }}
            disabled={pending}
            className="rounded-md border border-emerald-300 p-1 text-emerald-700 disabled:opacity-50 dark:border-emerald-900 dark:text-emerald-300"
          >
            {pending ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
            ) : (
              <Save className="size-3.5" aria-hidden />
            )}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="rounded-md border border-[var(--border)] p-1"
          >
            <X className="size-3.5" aria-hidden />
          </button>
        </div>
      </Row>
    );
  }

  return (
    <Row label={label}>
      <div className="flex items-start gap-2">
        <span className="flex-1 whitespace-pre-wrap text-sm">
          {value || <span className="opacity-50">{placeholder ?? "—"}</span>}
        </span>
        {!disabled && (
          <button
            type="button"
            onClick={() => {
              setDraft(value);
              setEditing(true);
            }}
            aria-label={`Edit ${label}`}
            className="rounded-md border border-[var(--border)] p-1 opacity-50 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/5"
          >
            <Pencil className="size-3.5" aria-hidden />
          </button>
        )}
      </div>
    </Row>
  );
}

function InlineSelectField({
  label,
  value,
  groups,
  onSave,
  pending,
  disabled,
}: {
  label: string;
  value: string;
  groups: { category: string; items: { code: string; label: string }[] }[];
  onSave: (v: string) => void;
  pending: boolean;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (editing && !disabled) {
    return (
      <Row label={label}>
        <div className="flex items-start gap-2">
          <select
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="flex-1 rounded-md border border-[var(--border)] bg-transparent px-2 py-1 text-sm outline-none focus:border-[var(--accent)]"
          >
            {groups.map((g) => (
              <optgroup key={g.category} label={g.category}>
                {g.items.map((i) => (
                  <option key={i.code} value={i.code}>
                    {i.code} — {i.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              if (draft !== value) onSave(draft);
              setEditing(false);
            }}
            disabled={pending}
            className="rounded-md border border-emerald-300 p-1 text-emerald-700 disabled:opacity-50 dark:border-emerald-900 dark:text-emerald-300"
          >
            {pending ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
            ) : (
              <Save className="size-3.5" aria-hidden />
            )}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="rounded-md border border-[var(--border)] p-1"
          >
            <X className="size-3.5" aria-hidden />
          </button>
        </div>
      </Row>
    );
  }

  return (
    <Row label={label}>
      <div className="flex items-start gap-2">
        <span className="flex-1 text-sm">{value}</span>
        {!disabled && (
          <button
            type="button"
            onClick={() => {
              setDraft(value);
              setEditing(true);
            }}
            aria-label={`Edit ${label}`}
            className="rounded-md border border-[var(--border)] p-1 opacity-50 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/5"
          >
            <Pencil className="size-3.5" aria-hidden />
          </button>
        )}
      </div>
    </Row>
  );
}
